import React, { useEffect } from 'react';
import styled from 'styled-components';
import { dataLayer, utilities } from '@weatherbug/common';

import { reduxConnect } from '../../common';
import { DailyForecastSummaryList } from './dailyForecastSummaryList';
import { IStdParameters, IStoreState } from '../common/interfaces/types';
import { translate } from '../../common/translation/translate';
import { CommonWidgetContainer } from '../common/widgets/commonWidgetStyle';
import { CommonWidgetHeader } from '../common/widgets/commonWidgetHeader';
import { WidgetErrorUI } from '../widgetErrorUI/WidgetErrorUI';
import { isDataError } from '../common/utilities/isDataError';
import { getWidgetHeaderText } from '../../common/utilities/getWidgetHeaderUtil';

const { temperatureUnits, unitSystems } = utilities.units;
const offset = 0;
const locationType = 'latitudelongitude';
const verbose = false;
const refresh = false;

const { getDailyForecastByLatLon } = dataLayer.actions;

interface DailyForecastSummaryWidgetProps {
    authId: string;
    secret: string;
    latitude: number;
    longitude: number;
    forecastBaseUrl: string;
    routeString: string;
    getDailyForecastByLatLon: typeof getDailyForecastByLatLon;
    tempUnit: number;
    cityName: string;
    stateAbbreviation: string;
    displayCompositeName: string;
    stdParameters: IStdParameters;
    forecastDataLoadError: boolean;
    lastError: Record<string, unknown>;
    enable10DayForecastPageOnWeb2: boolean;
    slugName: string;
}

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { units, appState, forecast } =  storeState || {};
    const { authId, secret, forecastBaseUrl, stdParameters, user, enable10DayForecastPageOnWeb2 } = appState?.appState || {};    
    const locality = user?.ActiveLocation?.Locality || {};
    const { CityName: cityName, TerritoryAbbr: stateAbbreviation, DisplayCompositeName: displayCompositeName, SlugName } = locality;
    const routeString = locality.RouteString;
    const { tempUnit } = units || {};
    const { lastError } = forecast ?? {};

    return {
        tempUnit,
        latitude: locality.Latitude,
        longitude: locality.Longitude,
        authId,
        secret,
        forecastBaseUrl,
        stdParameters,
        routeString,
        lastError,
        cityName,
        stateAbbreviation,
        displayCompositeName,
        enable10DayForecastPageOnWeb2,
        slugName: SlugName
    };
};

const mapDispatchToProps = {
    getDailyForecastByLatLon
};

export const DailyForecastSummaryWidget = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(({
    authId,
    secret,
    latitude,
    longitude,
    forecastBaseUrl,
    routeString,
    lastError,
    getDailyForecastByLatLon,
    stdParameters,
    cityName,
    stateAbbreviation,
    displayCompositeName,
    enable10DayForecastPageOnWeb2,
    slugName,
    tempUnit }: DailyForecastSummaryWidgetProps) => {

    const forecastDataLoadError = isDataError(lastError);

    useEffect(() => {
        try {
            if (authId && forecastBaseUrl && Number.isFinite(latitude) && Number.isFinite(longitude)) {
                let units = unitSystems.english;

                if (tempUnit && tempUnit === temperatureUnits.celsius) {
                    units = unitSystems.metric;
                }

                getDailyForecastByLatLon(
                    `${latitude},${longitude}`,
                    locationType,
                    offset,
                    units,
                    forecastBaseUrl,
                    authId,
                    secret,
                    stdParameters,
                    verbose,
                    refresh,
                    tempUnit,
                );
            }
        } catch (error) {
            console.error('Failed to fetch Forecast summary widget Data', error);
        }
    
    }, [ authId, secret, latitude, longitude, forecastBaseUrl, tempUnit ]);

    let route = `/weather-forecast/10-day-weather/${routeString}`;
    const headerText = cityName && stateAbbreviation ? getWidgetHeaderText({ widgetTitle:translate('weatherForecast'), cityName, stateAbbreviation }) : getWidgetHeaderText({ widgetTitle:translate('weatherForecast'), displayCompositeName }) ;
    
    if (enable10DayForecastPageOnWeb2 && slugName) {
        route = `https://${location.host}/weather-forecast/10-day-weather/${slugName}`;
        return (
            <CommonWidgetContainer target='_self' href={route}>
                <CommonWidgetHeader headerText={headerText} />
                <ForecastSummaryBodyWrapper>
                    {!forecastDataLoadError ? <DailyForecastSummaryList /> : <WidgetErrorUI />}
                </ForecastSummaryBodyWrapper>
            </CommonWidgetContainer>
        );
    }

    return (
        <CommonWidgetContainer href={route}>
            <CommonWidgetHeader headerText={headerText} />
            <ForecastSummaryBodyWrapper>
                {!forecastDataLoadError ? <DailyForecastSummaryList /> : <WidgetErrorUI />}
            </ForecastSummaryBodyWrapper>
        </CommonWidgetContainer>
    );
});

const ForecastSummaryBodyWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 213px;
`;
